import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import ReplyToField from "@/components/ReplyToField";
import { toast } from "sonner";

/**
 * SendDocDialog — confirmation step before emailing an invoice / estimate /
 * payment receipt. Always re-fetches the customer record on open so a
 * customer that was just created inline (e.g. from the Add Invoice dialog)
 * is recognised even when the local cache is stale.
 *
 * Props:
 *   doc        — { id, kind: "invoice" | "estimate" | "receipt", number,
 *                  customer_id (preferred), customer_name, customer_email }
 *                When null, dialog is closed.
 *   onClose()  — close handler
 *   onSent(d)  — fired with the API response body on success
 */
const ENDPOINT = {
    invoice: (id) => `/invoices/${id}/send`,
    estimate: (id) => `/invoices/${id}/send`,
    receipt: (id) => `/invoices/${id}/send-receipt`,
};

const VERB = {
    invoice: "Send invoice",
    estimate: "Send estimate",
    receipt: "Email receipt",
};

const RESEND_VERB = {
    invoice: "Resend invoice",
    estimate: "Resend estimate",
    receipt: "Resend receipt",
};

const DESC = {
    invoice: "Email this invoice to the customer.",
    estimate: "Email this estimate to the customer.",
    receipt: "Email a receipt for the latest payment to the customer.",
};

const RESEND_DESC = {
    invoice: "Send an updated copy of this invoice to the customer. Any recent edits (line items, totals, due date) will be included.",
    estimate: "Send an updated copy of this estimate to the customer. Any recent edits will be included.",
    receipt: "Send another copy of the payment receipt to the customer.",
};

export default function SendDocDialog({ doc, onClose, onSent }) {
    const [replyTo, setReplyTo] = useState(undefined);
    const [sending, setSending] = useState(false);
    // Fresh customer record fetched on dialog open so we never block on a
    // stale cache that lacks the email of an inline-created customer.
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!doc) {
            setCustomer(null);
            return;
        }
        setReplyTo(undefined);
        if (!doc.customer_id) {
            // Older callers don't pass customer_id — fall back to the inlined fields.
            setCustomer({ name: doc.customer_name, email: doc.customer_email });
            return;
        }
        setLoading(true);
        api.get(`/customers/${doc.customer_id}`)
            .then(({ data }) => setCustomer(data))
            .catch(() => {
                // 404 or network — fall back to whatever the parent passed.
                setCustomer({ name: doc.customer_name, email: doc.customer_email });
            })
            .finally(() => setLoading(false));
    }, [doc?.id, doc?.customer_id]);

    const customerEmail = customer?.email || null;
    const customerName = customer?.name || doc?.customer_name || "—";

    const send = async () => {
        if (!doc) return;
        if (!customerEmail) {
            toast.error("Customer has no email on file. Add one to their profile and try again.");
            return;
        }
        setSending(true);
        try {
            const body = { reply_to: (replyTo || "").trim() || null };
            const { data } = await api.post(ENDPOINT[doc.kind](doc.id), body);
            // /send returns { invoice, email: { sent, detail, to } }; /send-receipt returns { sent, detail, to }
            const sent = data?.email ? data.email.sent : data?.sent;
            const to = data?.email ? data.email.to : data?.to;
            const detail = data?.email ? data.email.detail : data?.detail;
            if (sent) {
                toast.success(`Sent to ${to}`);
                onSent?.(data);
                onClose?.();
            } else {
                toast.error(`Not sent — ${detail || "unknown error"}`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setSending(false); }
    };

    return (
        <Dialog open={!!doc} onOpenChange={(v) => { if (!sending && !v) onClose?.(); }}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="font-heading text-xl flex items-center gap-2">
                        <Send className="h-4 w-4 text-secondary" /> {doc ? (doc.resend ? RESEND_VERB : VERB)[doc.kind] : "Send"}
                    </DialogTitle>
                </DialogHeader>
                {doc && (
                    <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">{(doc.resend ? RESEND_DESC : DESC)[doc.kind]}</p>
                        <div className="rounded-2xl bg-sand-100 p-3 text-xs space-y-1">
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground uppercase tracking-wider">Document</span>
                                <span className="font-medium text-ocean-700">{doc.number || "—"}</span>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground uppercase tracking-wider">To</span>
                                <span className="font-medium text-ocean-700 truncate ml-2 text-right" data-testid="send-doc-recipient">
                                    {loading ? (
                                        <span className="inline-flex items-center gap-1.5 text-muted-foreground font-normal">
                                            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                        </span>
                                    ) : (
                                        <>
                                            {customerName}
                                            {customerEmail && (
                                                <span className="text-muted-foreground font-normal"> · {customerEmail}</span>
                                            )}
                                        </>
                                    )}
                                </span>
                            </div>
                        </div>
                        <ReplyToField value={replyTo} onChange={setReplyTo} testId="send-reply-to" />
                        {!loading && !customerEmail && (
                            <div className="rounded-2xl bg-coral/10 text-coral-dark text-xs px-3 py-2" data-testid="send-doc-no-email">
                                This customer has no email on file. Open the customer record and add one to enable sending.
                            </div>
                        )}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
                    <Button
                        onClick={send}
                        disabled={sending || loading || !customerEmail}
                        className="bg-ocean-700 text-white"
                        data-testid="send-doc-submit"
                    >
                        {sending ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Sending…</>)
                                 : (<><Send className="h-4 w-4 mr-1.5" /> {doc ? (doc.resend ? RESEND_VERB : VERB)[doc.kind] : "Send"}</>)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
